import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, FileText, Loader2 } from "lucide-react";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api, apiError } from "@/lib/api";

export default function DocumentAccess() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let url;
    api
      .get(`/public/documents/${token}`, { responseType: "blob" })
      .then(({ data }) => {
        url = URL.createObjectURL(data);
        setBlobUrl(url);
        setState("ready");
      })
      .catch(async (err) => {
        let message = apiError(err);
        const blob = err?.response?.data;
        if (blob instanceof Blob) {
          try {
            message = JSON.parse(await blob.text()).detail || message;
          } catch {
            /* keep default */
          }
        }
        setError(message);
        setState("error");
      });
    return () => url && URL.revokeObjectURL(url);
  }, [token]);

  return (
    <div className="flex min-h-screen flex-col items-start px-6 py-14 text-left sm:px-14">
      <Link to="/" className="mb-12">
        <Brand />
      </Link>
      <Card className="w-full max-w-2xl border-slate-200">
        <CardContent className="p-8" data-testid="document-access">
          {state === "loading" && (
            <div className="flex items-center gap-3 text-slate-700">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Opening your document…</span>
            </div>
          )}
          {state === "error" && (
            <>
              <h1 className="font-display text-2xl font-bold text-slate-900">
                This link is not available
              </h1>
              <p className="mt-3 text-sm text-slate-600" data-testid="document-error">
                {error}
              </p>
              <p className="mt-3 text-sm text-slate-600">
                Please contact the Alabama Church School office to have your document resent.
              </p>
            </>
          )}
          {state === "ready" && (
            <>
              <FileText className="h-9 w-9 text-blue-600" />
              <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">
                Annual Academic Record &amp; Report Card
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Your student's document issued by Alabama Church School is ready. Please save a copy
                for your records.
              </p>
              <a href={blobUrl} download="ACS-Annual-Academic-Record.pdf">
                <Button className="mt-6 gap-2" data-testid="document-download-button">
                  <Download className="h-4 w-4" /> Download PDF
                </Button>
              </a>
              <iframe
                title="Annual Academic Record"
                src={blobUrl}
                className="mt-6 h-[34rem] w-full rounded-md border border-slate-200"
                data-testid="document-viewer"
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
